import 'mapbox-gl/dist/mapbox-gl.css';

import mapboxgl, { Map as MapboxMap } from 'mapbox-gl';
import { useEffect, useRef, useState, useCallback } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { FaTruck } from 'react-icons/fa';

import { getMapboxAccessToken, getMapboxStyle } from '@/utils/urls';
import {
  subscribeToDriverLocation,
  DriverLocation,
  disconnectPusher,
} from '@/components/TrackingDetail/websocket';
import { TRACKING_CASES } from '@/components/TrackingDetail/constants';

const DENMARK_CENTER: [number, number] = [9.5018, 57.2639];
const MIN_ZOOM = 6;
const INITIAL_ZOOM = 6.5;
const MAX_ZOOM = 20;
const DRIVER_FOCUS_ZOOM = 16.5;
const DRIVER_FOLLOW_DURATION_MS = 600;
const DRIVER_MOVE_THRESHOLD = 0.0001;

const mapboxAccessToken = getMapboxAccessToken();
const mapboxStyle = getMapboxStyle();
const DRIVER_MARKER_COLOR = '#22c55e';

const DRIVER_MARKER_Z_INDEX = 1;
const RECIPIENT_MARKER_Z_INDEX = 50;
const ARRIVING_SOON_MARKER_Z_INDEX = 99999;

const bringMarkerToFront = (marker: mapboxgl.Marker | null) => {
  const element = marker?.getElement();
  const wrapper = element?.parentElement;

  if (wrapper?.parentElement) {
    wrapper.parentElement.appendChild(wrapper);
  }
};

const createDriverMarkerElement = () => {
  const el = document.createElement('div');
  el.className = 'driver-location-marker';
  el.style.cssText = `width:28px;height:28px;border-radius:50%;background:${DRIVER_MARKER_COLOR};display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);cursor:pointer;`;
  el.innerHTML = renderToStaticMarkup(
    <FaTruck color='white' size={14} style={{ display: 'block' }} />
  );

  return el;
};

const RECIPIENT_MARKER_HIDDEN_CASES = [
  TRACKING_CASES.registered,
  TRACKING_CASES.cancelled,
  TRACKING_CASES.departureFromDepot,
  TRACKING_CASES.arrivalAtDepot,
];

const isArrivingSoonCase = (selectedCase?: string | null) =>
  selectedCase === TRACKING_CASES.intervalPickup ||
  selectedCase === TRACKING_CASES.intervalDelivery;

const isDriverLocationCase = (selectedCase?: string | null) =>
  selectedCase === TRACKING_CASES.outForPickup ||
  selectedCase === TRACKING_CASES.intervalPickup ||
  selectedCase === TRACKING_CASES.outForDelivery ||
  selectedCase === TRACKING_CASES.intervalDelivery;

const createRecipientMarkerElement = (selectedCase?: string | null) => {
  const el = document.createElement('div');
  el.className = 'recipient-marker';
  el.style.cursor = 'pointer';

  if (
    selectedCase === TRACKING_CASES.pickedUp ||
    selectedCase === TRACKING_CASES.delivered
  ) {
    el.style.width = '30px';
    el.style.height = '36px';
    el.innerHTML = `
      <svg width="30" height="36" viewBox="0 0 30 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 0C6.716 0 0 6.716 0 15c0 10.5 15 21 15 21s15-10.5 15-21c0-8.284-6.716-15-15-15z" fill="#22C70D"/>
        <path d="M9 15.5L12.5 19L21 10.5" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    return { element: el, anchor: 'bottom' as const };
  }

  if (isArrivingSoonCase(selectedCase)) {
    el.className = 'recipient-marker arriving-soon-marker';
    el.style.width = '30px';
    el.style.height = '36px';
    el.innerHTML = `
      <svg width="30" height="36" viewBox="0 0 30 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 0C6.716 0 0 6.716 0 15c0 10.5 15 21 15 21s15-10.5 15-21c0-8.284-6.716-15-15-15z" fill="#449486"/>
        <circle cx="15" cy="14" r="6" stroke="white" stroke-width="2" fill="none"/>
        <path d="M15 11v3.5l2.25 1.75" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    return { element: el, anchor: 'bottom' as const };
  }

  if (selectedCase === TRACKING_CASES.postponed) {
    el.style.width = '30px';
    el.style.height = '36px';
    el.innerHTML = `
      <svg width="30" height="36" viewBox="0 0 30 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 0C6.716 0 0 6.716 0 15c0 10.5 15 21 15 21s15-10.5 15-21c0-8.284-6.716-15-15-15z" fill="#DC3434"/>
        <circle cx="15" cy="14" r="6" stroke="white" stroke-width="2" fill="none"/>
        <path d="M15 11v3.5l2.25 1.75" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    return { element: el, anchor: 'bottom' as const };
  }

  el.style.width = '20px';
  el.style.height = '20px';
  el.style.borderRadius = '50%';
  el.style.backgroundColor = '#449486';
  el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';

  return { element: el, anchor: 'center' as const };
};

const parseCoordinate = (value: string | number | null | undefined) => {
  if (value == null) return null;

  const parsed = typeof value === 'string' ? parseFloat(value) : value;

  if (typeof parsed !== 'number' || isNaN(parsed) || parsed === 0) {
    return null;
  }

  return parsed;
};

type MapProps = {
  className?: string;
  driverId?: string | null;
  recipientLatitude?: string | number | null;
  recipientLongitude?: string | number | null;
  selectedCase?: string | null;
  trackingNumber?: string | null;
  onDriverLocationChange?: (location: DriverLocation | null) => void;
};

const Map = ({
  className,
  driverId,
  recipientLatitude,
  recipientLongitude,
  selectedCase,
  trackingNumber,
  onDriverLocationChange,
}: MapProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const driverMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const recipientMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [driverLocation, setDriverLocation] = useState<DriverLocation | null>(
    null
  );
  const [mapLoaded, setMapLoaded] = useState<boolean>(false);
  const cameraInitializedRef = useRef<boolean>(false);
  const driverCameraFocusedRef = useRef<boolean>(false);
  const driverLocationRef = useRef<DriverLocation | null>(null);

  const keepArrivingSoonMarkerOnTop = useCallback(() => {
    if (!isArrivingSoonCase(selectedCase)) return;
    bringMarkerToFront(recipientMarkerRef.current);
  }, [selectedCase]);

  useEffect(() => {
    onDriverLocationChange?.(driverLocation);
  }, [driverLocation, onDriverLocationChange]);

  useEffect(() => {
    driverLocationRef.current = driverLocation;
  }, [driverLocation]);

  useEffect(() => {
    cameraInitializedRef.current = false;
    driverCameraFocusedRef.current = false;
  }, [driverId, selectedCase]);

  const getRecipientZoom = (trackingCase?: string | null) => {
    if (
      trackingCase === TRACKING_CASES.pickedUp ||
      trackingCase === TRACKING_CASES.delivered ||
      trackingCase === TRACKING_CASES.postponed ||
      isArrivingSoonCase(trackingCase)
    ) {
      return 10;
    }

    return 12;
  };

  const initializeCamera = useCallback(() => {
    if (!mapRef.current || cameraInitializedRef.current) {
      return;
    }

    const recipientLat = parseCoordinate(recipientLatitude);
    const recipientLng = parseCoordinate(recipientLongitude);
    const shouldShowRecipientMarker = !RECIPIENT_MARKER_HIDDEN_CASES.includes(
      selectedCase as string
    );

    if (
      recipientLat != null &&
      recipientLng != null &&
      shouldShowRecipientMarker
    ) {
      mapRef.current.jumpTo({
        center: [recipientLng, recipientLat],
        zoom: getRecipientZoom(selectedCase),
      });
      cameraInitializedRef.current = true;
      return;
    }

    const driverLocationSnapshot = driverLocationRef.current;
    const driverLng = driverLocationSnapshot?.longitude;
    const driverLat = driverLocationSnapshot?.latitude;
    const hasDriverCoords =
      isDriverLocationCase(selectedCase) &&
      typeof driverLng === 'number' &&
      typeof driverLat === 'number' &&
      !isNaN(driverLng) &&
      !isNaN(driverLat) &&
      driverLng !== 0 &&
      driverLat !== 0;

    if (hasDriverCoords) {
      mapRef.current.jumpTo({
        center: [driverLng, driverLat],
        zoom: DRIVER_FOCUS_ZOOM,
      });
      cameraInitializedRef.current = true;
    }
  }, [recipientLatitude, recipientLongitude, selectedCase]);

  const focusOnDriverOnce = useCallback(
    (driverCoords: [number, number]) => {
      if (
        !mapRef.current ||
        !isDriverLocationCase(selectedCase) ||
        driverCameraFocusedRef.current
      ) {
        return;
      }

      mapRef.current.flyTo({
        center: driverCoords,
        zoom: DRIVER_FOCUS_ZOOM,
        duration: 800,
      });

      driverCameraFocusedRef.current = true;
    },
    [selectedCase]
  );

  const followDriver = useCallback(
    (driverCoords: [number, number]) => {
      if (
        !mapRef.current ||
        !isDriverLocationCase(selectedCase) ||
        !driverCameraFocusedRef.current
      ) {
        return;
      }

      mapRef.current.easeTo({
        center: driverCoords,
        duration: DRIVER_FOLLOW_DURATION_MS,
      });
    },
    [selectedCase]
  );

  useEffect(() => {
    if (!mapLoaded || !mapRef.current) {
      return;
    }

    initializeCamera();
  }, [mapLoaded, initializeCamera]);

  const updateDriverMarker = useCallback(
    (location: DriverLocation) => {
      if (!mapRef.current || !location) return;

      const lng = location.longitude;
      const lat = location.latitude;

      // Validate coordinates
      if (
        typeof lng !== 'number' ||
        typeof lat !== 'number' ||
        isNaN(lng) ||
        isNaN(lat) ||
        lng === 0 ||
        lat === 0
      ) {
        return;
      }

      const coordinates: [number, number] = [lng, lat];

      // If marker exists, smoothly animate to new position
      if (driverMarkerRef.current) {
        // Get current position
        const currentPos = driverMarkerRef.current.getLngLat();
        const currentCoords: [number, number] = [
          currentPos.lng,
          currentPos.lat,
        ];

        // Only update if position actually changed
        const distance = Math.sqrt(
          Math.pow(currentCoords[0] - coordinates[0], 2) +
            Math.pow(currentCoords[1] - coordinates[1], 2)
        );

        if (distance > DRIVER_MOVE_THRESHOLD) {
          // Cancel any existing animation
          if (animationFrameRef.current !== null) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
          }

          // Ultra smooth continuous animation using requestAnimationFrame
          const startPos = { lng: currentCoords[0], lat: currentCoords[1] };
          const endPos = { lng: coordinates[0], lat: coordinates[1] };
          const duration = DRIVER_FOLLOW_DURATION_MS;
          const startTime = performance.now();

          const animate = (currentTime: number) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Smooth ease-out quadratic for continuous movement feeling
            // Starts immediately and smoothly decelerates - no pause
            const easeOut = 1 - (1 - progress) * (1 - progress);

            const currentLng =
              startPos.lng + (endPos.lng - startPos.lng) * easeOut;
            const currentLat =
              startPos.lat + (endPos.lat - startPos.lat) * easeOut;

            if (driverMarkerRef.current && mapRef.current) {
              driverMarkerRef.current.setLngLat([currentLng, currentLat]);
            }

            if (progress < 1) {
              animationFrameRef.current = requestAnimationFrame(animate);
            } else {
              // Ensure final position is exact
              if (driverMarkerRef.current) {
                driverMarkerRef.current.setLngLat(coordinates);
              }
              animationFrameRef.current = null;
              keepArrivingSoonMarkerOnTop();
            }
          };

          animationFrameRef.current = requestAnimationFrame(animate);
        }

        keepArrivingSoonMarkerOnTop();
        return;
      }

      // Create new marker - green circle with truck icon (matches intertrace-web-app)
      const driverEl = createDriverMarkerElement();

      const driverMarker = new (mapboxgl as any).Marker({ element: driverEl })
        .setLngLat(coordinates)
        .addTo(mapRef.current);

      driverMarkerRef.current = driverMarker;
      keepArrivingSoonMarkerOnTop();
    },
    [keepArrivingSoonMarkerOnTop]
  );

  // Initialize map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) {
      return;
    }

    mapboxgl.accessToken = mapboxAccessToken;

    const style = document.createElement('style');
    style.textContent = `
      .mapboxgl-map .mapboxgl-marker:has(.driver-location-marker) {
        z-index: ${DRIVER_MARKER_Z_INDEX} !important;
      }
      .mapboxgl-map .mapboxgl-marker:has(.recipient-marker):not(:has(.arriving-soon-marker)) {
        z-index: ${RECIPIENT_MARKER_Z_INDEX} !important;
      }
      .mapboxgl-map .mapboxgl-marker:has(.arriving-soon-marker) {
        z-index: ${ARRIVING_SOON_MARKER_Z_INDEX} !important;
      }
    `;
    document.head.appendChild(style);

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: mapboxStyle,
      center: DENMARK_CENTER,
      zoom: INITIAL_ZOOM,
      attributionControl: false,
    });

    // Enable map interactions - allow pan and zoom
    // dragPan is enabled by default, so we don't disable it
    (map as any).dragRotate.disable();
    // scrollZoom is enabled by default
    (map as any).boxZoom.disable();
    (map as any).doubleClickZoom.disable();
    (map as any).keyboard.disable();
    // touchZoomRotate is enabled by default

    // Limit zoom so the map stays at regional/country level
    (map as any).setMinZoom(MIN_ZOOM);
    (map as any).setMaxZoom(MAX_ZOOM);

    (map as any).on('load', () => {
      setMapLoaded(true);
      // Ensure zoom is enabled after map loads
      if ((map as any).scrollZoom) {
        (map as any).scrollZoom.enable();
      }
      if ((map as any).touchZoomRotate) {
        (map as any).touchZoomRotate.enable();
      }
      console.log('🗺️ Map loaded, zoom enabled:', {
        scrollZoom: (map as any).scrollZoom?.isEnabled(),
        touchZoomRotate: (map as any).touchZoomRotate?.isEnabled(),
      });
    });

    // Also check if map is already loaded
    if ((map as any).loaded()) {
      setMapLoaded(true);
      // Ensure zoom is enabled if map is already loaded
      if ((map as any).scrollZoom) {
        (map as any).scrollZoom.enable();
      }
      if ((map as any).touchZoomRotate) {
        (map as any).touchZoomRotate.enable();
      }
    }

    mapRef.current = map;

    return () => {
      if (style && document.head.contains(style)) {
        document.head.removeChild(style);
      }

      // Clean up driver marker
      if (driverMarkerRef.current) {
        try {
          driverMarkerRef.current.remove();
        } catch (error) {
          console.error('Error removing driver marker:', error);
        }
        driverMarkerRef.current = null;
      }

      // Clean up recipient marker
      if (recipientMarkerRef.current) {
        try {
          recipientMarkerRef.current.remove();
        } catch (error) {
          console.error('Error removing recipient marker:', error);
        }
        recipientMarkerRef.current = null;
      }

      // Cancel any pending animation
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Subscribe to driver location updates via WebSocket
  useEffect(() => {
    const shouldSubscribe = isDriverLocationCase(selectedCase);

    if (!driverId || !mapLoaded || !shouldSubscribe) {
      if (driverMarkerRef.current) {
        driverMarkerRef.current.remove();
        driverMarkerRef.current = null;
      }
      setDriverLocation(null);
      return;
    }

    console.log('🔌 WebSocket subscription enabled for driver location');
    const unsubscribe = subscribeToDriverLocation(
      driverId,
      trackingNumber,
      (location) => {
        setDriverLocation(location);
      },
      (error) => {
        console.error('WebSocket error:', error);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [driverId, mapLoaded, selectedCase, trackingNumber]);

  // Update driver marker when location changes
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !driverLocation) {
      if (driverMarkerRef.current) {
        driverMarkerRef.current.remove();
        driverMarkerRef.current = null;
      }

      if (!driverLocation) {
        driverCameraFocusedRef.current = false;
      }

      return;
    }

    const hadDriverMarker = Boolean(driverMarkerRef.current);
    const previousCoords = hadDriverMarker
      ? ([
          driverMarkerRef.current!.getLngLat().lng,
          driverMarkerRef.current!.getLngLat().lat,
        ] as [number, number])
      : null;

    updateDriverMarker(driverLocation);

    const driverCoords: [number, number] = [
      driverLocation.longitude,
      driverLocation.latitude,
    ];

    if (!hadDriverMarker) {
      focusOnDriverOnce(driverCoords);
    } else if (previousCoords) {
      const distance = Math.sqrt(
        Math.pow(previousCoords[0] - driverCoords[0], 2) +
          Math.pow(previousCoords[1] - driverCoords[1], 2)
      );

      if (distance > DRIVER_MOVE_THRESHOLD) {
        followDriver(driverCoords);
      }
    }

    if (!cameraInitializedRef.current) {
      initializeCamera();
    }
  }, [
    driverLocation,
    mapLoaded,
    updateDriverMarker,
    initializeCamera,
    focusOnDriverOnce,
    followDriver,
  ]);

  // Add recipient marker when coordinates are available
  // Don't show marker in 'registered' state
  useEffect(() => {
    console.log('📍 Recipient marker effect triggered:', {
      mapLoaded,
      hasMap: !!mapRef.current,
      recipientLatitude,
      recipientLongitude,
      selectedCase,
    });

    // Don't show recipient marker in these cases
    const shouldShowMarker = !RECIPIENT_MARKER_HIDDEN_CASES.includes(
      selectedCase as string
    );

    if (!mapLoaded || !mapRef.current || !shouldShowMarker) {
      console.log('⚠️ Recipient marker conditions not met, removing marker');
      // Remove marker if conditions not met
      if (recipientMarkerRef.current) {
        recipientMarkerRef.current.remove();
        recipientMarkerRef.current = null;
      }
      return;
    }

    const lat = parseCoordinate(recipientLatitude);
    const lng = parseCoordinate(recipientLongitude);

    if (lat == null || lng == null) {
      console.warn('❌ Invalid recipient coordinates:', { lng, lat });
      return;
    }

    const coordinates: [number, number] = [lng, lat];
    console.log('✅ Creating recipient marker at:', coordinates);

    // Remove existing marker if it exists
    if (recipientMarkerRef.current) {
      recipientMarkerRef.current.remove();
    }

    // Create recipient marker
    const { element, anchor } = createRecipientMarkerElement(selectedCase);

    const recipientMarker = new (mapboxgl as any).Marker({
      element,
      anchor,
    })
      .setLngLat(coordinates)
      .addTo(mapRef.current);

    recipientMarkerRef.current = recipientMarker;
    keepArrivingSoonMarkerOnTop();
    console.log('✅ Recipient marker created successfully at:', coordinates);

    initializeCamera();
  }, [
    mapLoaded,
    recipientLatitude,
    recipientLongitude,
    selectedCase,
    keepArrivingSoonMarkerOnTop,
    initializeCamera,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnectPusher();
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ width: '100%', height: '100%' }}
    />
  );
};

export default Map;
